import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Video, ArrowLeft, Play, Square, Users, Clock, Monitor,
  Radio, Eye, CheckCircle2, AlertTriangle, Calendar, BookOpen, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });

export default function LiveRoomPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);
  const [joined, setJoined] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSession(); }, [sessionId]);

  const loadSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/live-sessions/${sessionId}`, { headers: getHeaders() });
      if (res.ok) setSession(await res.json());
      else toast.error('Oturum bulunamadi');
    } catch { toast.error('Baglanti hatasi'); }
    finally { setLoading(false); }
  };

  const handleStart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/live-sessions/${sessionId}/start`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (res.ok) { setJoinUrl(data.join_url); setJoined(true); loadSession(); toast.success('Oturum baslatildi!'); }
      else toast.error(data.detail || 'Baslatilamadi');
    } catch { toast.error('Sunucuya erisilemedi'); }
    finally { setActionLoading(false); }
  };

  const handleJoin = async () => {
    setActionLoading(true);
    try {
      toast.info('Derse baglaniliyor...');
      const res = await fetch(`${API_URL}/api/live-sessions/${sessionId}/join`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (res.ok) {
        // Meeting zaten canlı - kısa bekleme yeterli
        await new Promise(r => setTimeout(r, 1000));
        setJoinUrl(data.join_url);
        setJoined(true);
        toast.success('Derse katildiniz');
      } else toast.error(data.detail || 'Katilinamadi');
    } catch { toast.error('Baglanti hatasi'); }
    finally { setActionLoading(false); }
  };

  // Auto-start+join for instructor entering a scheduled session
  const handleStartAndJoin = async () => {
    setActionLoading(true);
    try {
      const startRes = await fetch(`${API_URL}/api/live-sessions/${sessionId}/start`, { method: 'POST', headers: getHeaders() });
      const startData = await startRes.json();
      if (startRes.ok) {
        toast.success('Ders baslatildi! Odaya baglaniliyor...');
        // BBB docs: "wait 5 seconds before showing iframe"
        await new Promise(r => setTimeout(r, 5000));
        setJoinUrl(startData.join_url);
        setJoined(true);
        loadSession();
      } else {
        toast.error(startData.detail || 'Baslatilamadi');
      }
    } catch { toast.error('Sunucuya erisilemedi'); }
  };

  const handleEnd = async () => {
    if (!window.confirm('Oturumu bitirmek istediginize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/live-sessions/${sessionId}/end`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { setJoined(false); setJoinUrl(null); loadSession(); toast.success('Oturum tamamlandi'); }
    } catch { toast.error('Hata'); }
  };

  const handleVideoComplete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/live-sessions/${sessionId}/video-complete`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (res.ok) { toast.success('Video izleme kaydedildi'); loadSession(); }
      else toast.error(data.detail || 'Hata');
    } catch { toast.error('Hata'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Video className="w-10 h-10 text-primary animate-pulse" />
    </div>
  );

  if (!session) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Card><CardContent className="p-8 text-center"><AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" /><p>Oturum bulunamadi</p>
        <Button className="mt-4" onClick={() => navigate('/live-sessions')}>Geri Don</Button>
      </CardContent></Card>
    </div>
  );

  const isInstructor = session.is_instructor;
  const isLive = session.status === 'live';
  const isCompleted = session.status === 'completed';
  const isScheduled = session.status === 'scheduled';
  const hasRecording = session.recording_url || session.recording_mp4_url;

  // ==================== LIVE VIEW (iframe or new tab) ====================
  if (joined && joinUrl) {
    return (
      <div className="h-screen flex flex-col bg-background" data-testid="live-room">
        <header className="flex items-center justify-between h-12 px-4 border-b bg-background/95 shrink-0">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-red-500 animate-pulse" />
            <span className="font-bold text-sm">{session.title}</span>
            <Badge variant="destructive" className="text-xs">CANLI</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" />{session.participant_count} katilimci</Badge>
            <Button size="sm" variant="outline" onClick={() => window.open(joinUrl, '_blank')}>
              Yeni Sekmede Ac
            </Button>
            {isInstructor && (
              <Button size="sm" variant="destructive" onClick={handleEnd} data-testid="end-session-btn">
                <Square className="w-3 h-3 mr-1" />Bitir
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => { setJoined(false); setJoinUrl(null); }}>
              <ArrowLeft className="w-3 h-3 mr-1" />Cik
            </Button>
          </div>
        </header>
        <iframe
          src={joinUrl}
          className="flex-1 w-full border-0"
          allow="camera *;microphone *;display-capture *;"
          allowFullScreen
          data-testid="bbb-iframe"
        />
      </div>
    );
  }

  // ==================== SESSION DETAIL VIEW ====================
  return (
    <div className="min-h-screen bg-background" data-testid="session-detail">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between h-16 px-4 max-w-4xl">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/live-sessions')}><ArrowLeft className="w-5 h-5" /></Button>
            <Video className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold">{session.title}</h1>
          </div>
          <Badge className={isLive ? 'bg-red-600' : isCompleted ? 'bg-emerald-600' : 'bg-blue-600'}>
            {isLive ? 'CANLI' : isCompleted ? 'Tamamlandi' : 'Planlanmis'}
          </Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Session Info */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Calendar className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-sm font-medium">{session.scheduled_at ? new Date(session.scheduled_at).toLocaleDateString('tr-TR') : '-'}</div>
                <div className="text-xs text-muted-foreground">{session.scheduled_at ? new Date(session.scheduled_at).toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'}) : ''}</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Clock className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-sm font-medium">{session.duration_minutes} dk</div>
                <div className="text-xs text-muted-foreground">Sure</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Users className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-sm font-medium">{session.participant_count}</div>
                <div className="text-xs text-muted-foreground">Katilimci</div>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <BookOpen className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-sm font-medium">{session.module_ids?.length || 0}</div>
                <div className="text-xs text-muted-foreground">Modul</div>
              </div>
            </div>

            {session.description && <p className="text-sm text-muted-foreground mb-4">{session.description}</p>}

            {/* Instructor info */}
            {session.instructor && (
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-primary" />
                <span className="text-sm">Öğretmen: <strong>{session.instructor.name}</strong></span>
              </div>
            )}

            {/* Module badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {session.module_ids?.map(mid => <Badge key={mid} variant="outline">{mid}</Badge>)}
            </div>

            {/* Exemption info */}
            <div className="p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground space-y-1">
              <div>{session.exempt_lessons ? '✅' : '❌'} Canli katilim → Ders tamamlama</div>
              <div>{session.exempt_quizzes ? '✅' : '❌'} Canli katilim → Quiz muafiyet</div>
              <div>{session.video_exempt_lessons ? '✅' : '❌'} Video izleme → Ders tamamlama</div>
              <div>{session.video_exempt_quizzes ? '✅' : '❌'} Video izleme → Quiz muafiyet</div>
              <div>Min. katilim: %{session.min_attendance_percent}</div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              {isScheduled && isInstructor && (
                <Button className="flex-1" onClick={handleStartAndJoin} size="lg" disabled={actionLoading} data-testid="start-btn">
                  {actionLoading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Baslatiliyor...</> : <><Play className="w-5 h-5 mr-2" />Dersi Baslat ve Gir</>}
                </Button>
              )}
              {isLive && !joined && (
                <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleJoin} size="lg" disabled={actionLoading} data-testid="join-btn">
                  {actionLoading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Baglaniliyor...</> : <><Monitor className="w-5 h-5 mr-2" />{isInstructor ? 'Odaya Git' : 'Derse Katil'}</>}
                </Button>
              )}
              {isLive && isInstructor && !joined && (
                <Button variant="destructive" onClick={handleEnd}><Square className="w-4 h-4 mr-2" />Bitir</Button>
              )}
              {isCompleted && hasRecording && (
                <Button className="flex-1" variant="outline" onClick={() => window.open(session.recording_mp4_url || session.recording_url, '_blank')}>
                  <Eye className="w-4 h-4 mr-2" />Kaydi Izle
                </Button>
              )}
              {isCompleted && hasRecording && (
                <Button onClick={handleVideoComplete} data-testid="video-complete-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" />Izledim
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Participants (Instructor view) */}
        {isInstructor && session.participants?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Katilimcilar ({session.participants.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[300px] overflow-y-auto">
                {session.participants.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2">
                    <div>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.is_mandatory && <Badge variant="outline" className="text-xs text-red-500 border-red-500/30">Zorunlu</Badge>}
                      <Badge className={
                        p.attendance_status === 'attended' ? 'bg-emerald-600' :
                        p.attendance_status === 'video_completed' ? 'bg-blue-600' :
                        p.attendance_status === 'partial' ? 'bg-amber-600' : 'bg-muted text-muted-foreground'
                      }>
                        {p.attendance_status === 'attended' ? 'Katildi' :
                         p.attendance_status === 'video_completed' ? 'Video' :
                         p.attendance_status === 'partial' ? 'Kismi' : 'Bekleniyor'}
                      </Badge>
                      {p.lessons_exempted && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
